import React from 'react';

// Static descriptor arrays - defined outside component to prevent recreation
const HOT_ZONE_DESCRIPTORS = [
  'Constant firefighting',
  'Burnout & fatigue',
  'Chaotic decision-making',
  'Rapid, uncontrolled growth',
  'Budget overruns',
];

const GOLDILOCKS_ZONE_DESCRIPTORS = [
  'Psychological safety',
  'Empowered leadership',
  'Calculated risk-taking',
  'Collective intelligence',
  'Deliberate innovation',
];

const COLD_ZONE_DESCRIPTORS = [
  'Siloed departments',
  'Risk aversion',
  'Compliance-driven culture',
  'Stifled innovation',
  'Disengaged workforce',
];

const GoldilocksZoneDiagram = () => {
  return (
    <div 
      className="goldilocks-diagram-container"
      data-testid="goldilocks-zone-interactive"
      style={{
        background: '#0E1018',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '4px',
        position: 'relative',
        overflow: 'hidden',
        padding: '48px 32px 32px',
      }}
    >
      {/* Ambient Glow Background */}
      <div 
        style={{
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(ellipse 60% 50% at 15% 40%, rgba(232,133,74,0.04) 0%, transparent 70%),
            radial-gradient(ellipse 50% 45% at 50% 35%, rgba(109,191,160,0.035) 0%, transparent 65%),
            radial-gradient(ellipse 60% 50% at 85% 40%, rgba(123,164,196,0.04) 0%, transparent 70%)
          `,
          pointerEvents: 'none',
        }}
      />

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '48px', position: 'relative', zIndex: 1 }}>
        {/* Overline */}
        <p style={{
          fontFamily: 'Outfit, sans-serif',
          fontSize: '11px',
          fontWeight: 300,
          letterSpacing: '3px',
          textTransform: 'uppercase',
          color: 'rgba(232,230,225,0.35)',
          marginBottom: '12px',
        }}>
          A Framework for Organisational Transformation
        </p>
        
        {/* Title */}
        <h2 style={{
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontSize: '38px',
          fontWeight: 600,
          color: '#E8E6E1',
          marginBottom: '12px',
          lineHeight: 1.2,
        }}>
          The Goldilocks Zone
        </h2>
        
        {/* Tagline */}
        <p style={{
          fontFamily: 'Outfit, sans-serif',
          fontSize: '15px',
          fontWeight: 300,
          color: 'rgba(232,230,225,0.6)',
        }}>
          Managing the environment for change — not the change itself
        </p>
      </div>

      {/* Three Zone Columns */}
      <div 
        className="zones-container"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '24px',
          marginBottom: '48px',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Zone 1 - Too Hot */}
        <ZoneColumn
          zoneLabel="Crisis Management"
          zoneTag="Too Hot"
          tagColor="#E8854A"
          glowColor="rgba(232,133,74,0.15)"
          planet={<HotPlanet />}
          descriptors={HOT_ZONE_DESCRIPTORS}
        />

        {/* Zone 2 - The Goldilocks Zone */}
        <ZoneColumn
          zoneLabel="Cultural Evolution"
          zoneTag="The Goldilocks Zone"
          tagColor="#6DBFA0"
          glowColor="rgba(109,191,160,0.12)"
          planet={<EarthPlanet />}
          descriptors={GOLDILOCKS_ZONE_DESCRIPTORS}
        />

        {/* Zone 3 - Too Cold */}
        <ZoneColumn
          zoneLabel="Commanded Complacency"
          zoneTag="Too Cold"
          tagColor="#7BA4C4"
          glowColor="rgba(123,164,196,0.12)"
          planet={<ColdPlanet />}
          descriptors={COLD_ZONE_DESCRIPTORS}
        />
      </div>

      {/* Spectrum Bar */}
      <div style={{ position: 'relative', zIndex: 1, marginBottom: '32px' }}>
        {/* Glow behind bar */}
        <div style={{
          position: 'absolute',
          top: '-4px',
          left: 0,
          right: 0,
          height: '11px',
          background: 'linear-gradient(to right, rgba(232,133,74,0.25) 0%, rgba(232,133,74,0.1) 25%, rgba(109,191,160,0.2) 45%, rgba(109,191,160,0.2) 55%, rgba(123,164,196,0.1) 75%, rgba(123,164,196,0.25) 100%)',
          filter: 'blur(4px)',
          borderRadius: '2px',
        }} />
        
        {/* Bar */}
        <div style={{
          height: '3px',
          borderRadius: '2px',
          background: 'linear-gradient(to right, #E8854A 0%, rgba(232,133,74,0.3) 25%, #6DBFA0 45%, #6DBFA0 55%, rgba(123,164,196,0.3) 75%, #7BA4C4 100%)',
          opacity: 0.7,
          position: 'relative',
        }} />

        {/* Labels below bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '12px',
          padding: '0 16px',
        }}>
          <span style={{
            fontFamily: 'Outfit, sans-serif',
            fontSize: '10px',
            fontWeight: 300,
            letterSpacing: '2px',
            textTransform: 'uppercase',
            color: 'rgba(232,230,225,0.35)',
          }}>
            Inhospitable Zone
          </span>
          <span style={{
            fontFamily: 'Outfit, sans-serif',
            fontSize: '10px',
            fontWeight: 300,
            letterSpacing: '2px',
            textTransform: 'uppercase',
            color: 'rgba(109,191,160,0.7)',
          }}>
            Habitable Zone
          </span>
          <span style={{
            fontFamily: 'Outfit, sans-serif',
            fontSize: '10px',
            fontWeight: 300,
            letterSpacing: '2px',
            textTransform: 'uppercase',
            color: 'rgba(232,230,225,0.35)',
          }}>
            Inhospitable Zone
          </span>
        </div>
      </div>

      {/* Insight Line */}
      <div style={{
        borderTop: '1px solid rgba(255,255,255,0.06)',
        paddingTop: '24px',
        marginBottom: '24px',
        position: 'relative',
        zIndex: 1,
      }}>
        <p style={{
          fontFamily: 'Outfit, sans-serif',
          fontSize: '13px',
          fontWeight: 300,
          fontStyle: 'italic',
          color: 'rgba(232,230,225,0.7)',
          lineHeight: 1.75,
          textAlign: 'center',
          maxWidth: '720px',
          margin: '0 auto',
        }}>
          Organisations are complex adaptive systems. The zones are not binary — different parts of the same organisation may occupy different positions on the spectrum simultaneously.
        </p>
      </div>

      {/* Footer */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative',
        zIndex: 1,
        paddingTop: '16px',
      }}>
        <span style={{
          fontFamily: 'Outfit, sans-serif',
          fontSize: '11px',
          fontWeight: 300,
          letterSpacing: '1px',
          color: 'rgba(232,230,225,0.35)',
        }}>
          The Goldilocks Zone Model ©
        </span>
        <span style={{
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontSize: '14px',
          fontWeight: 500,
          color: 'rgba(232,230,225,0.6)',
        }}>
          Nadir Khan & Craig Kimball
        </span>
      </div>

      {/* Mobile Responsive Styles */}
      <style>{`
        @media (max-width: 768px) {
          .zones-container {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }
        }
      `}</style>
    </div>
  );
};

// Zone Column Component
const ZoneColumn = ({ zoneLabel, zoneTag, tagColor, glowColor, planet, descriptors }) => {
  return (
    <div style={{
      position: 'relative',
      textAlign: 'center',
      padding: '24px 16px',
    }}>
      {/* Background Glow */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: `radial-gradient(ellipse 100% 80% at 50% 30%, ${glowColor} 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />

      {/* Planet */}
      <div style={{ 
        marginBottom: '24px', 
        position: 'relative', 
        zIndex: 1,
        display: 'flex',
        justifyContent: 'center',
      }}>
        {planet}
      </div>

      {/* Zone Tag */}
      <p style={{
        fontFamily: 'Outfit, sans-serif',
        fontSize: '10px',
        fontWeight: 400,
        letterSpacing: '2px',
        textTransform: 'uppercase',
        color: tagColor,
        marginBottom: '8px',
        position: 'relative',
        zIndex: 1,
      }}>
        {zoneTag}
      </p>

      {/* Zone Label */}
      <h3 style={{
        fontFamily: '"Cormorant Garamond", Georgia, serif',
        fontSize: '20px',
        fontWeight: 600,
        color: '#E8E6E1',
        marginBottom: '20px',
        position: 'relative',
        zIndex: 1,
      }}>
        {zoneLabel}
      </h3>

      {/* Descriptors */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {descriptors.map((desc, idx) => (
          <p key={`desc-${zoneLabel}-${idx}`} style={{
            fontFamily: 'Outfit, sans-serif',
            fontSize: '13.5px',
            fontWeight: 300,
            color: 'rgba(232,230,225,0.6)',
            lineHeight: 2,
            margin: 0,
          }}>
            {desc}
          </p>
        ))}
      </div>
    </div>
  );
};

// Hot Planet (Mars-like)
const HotPlanet = () => (
  <div style={{
    width: '140px',
    height: '140px',
    borderRadius: '50%',
    position: 'relative',
    background: 'radial-gradient(circle at 35% 35%, #F4A460 0%, #D4763A 40%, #7A3A14 100%)',
    boxShadow: '0 0 40px 8px rgba(232,133,74,0.3), inset -8px -8px 20px rgba(0,0,0,0.4)',
    overflow: 'hidden',
  }}>
    {/* Surface texture - hot planet bands */}
    <div style={{
      position: 'absolute',
      inset: 0,
      borderRadius: '50%',
      background: `
        radial-gradient(ellipse 120% 20% at 50% 25%, rgba(244,164,96,0.4) 0%, transparent 50%),
        radial-gradient(ellipse 100% 15% at 45% 45%, rgba(180,100,60,0.3) 0%, transparent 50%),
        radial-gradient(ellipse 90% 18% at 55% 65%, rgba(212,118,58,0.35) 0%, transparent 50%),
        radial-gradient(ellipse 110% 12% at 48% 80%, rgba(140,70,30,0.3) 0%, transparent 50%)
      `,
    }} />
    {/* Atmosphere highlight */}
    <div style={{
      position: 'absolute',
      top: '8%',
      left: '15%',
      width: '35%',
      height: '25%',
      borderRadius: '50%',
      background: 'radial-gradient(ellipse at center, rgba(255,200,150,0.25) 0%, transparent 70%)',
    }} />
  </div>
);

// Earth Planet (Goldilocks Zone)
const EarthPlanet = () => (
  <div style={{
    width: '140px',
    height: '140px',
    borderRadius: '50%',
    position: 'relative',
    background: 'radial-gradient(circle at 35% 35%, #8CC5E0 0%, #4A8FA8 35%, #2A6B4E 65%, #1A4A35 100%)',
    boxShadow: '0 0 40px 8px rgba(109,191,160,0.2), inset -8px -8px 20px rgba(0,0,0,0.3)',
    overflow: 'hidden',
  }}>
    {/* Continents simulation */}
    <div style={{
      position: 'absolute',
      inset: 0,
      borderRadius: '50%',
      background: `
        radial-gradient(ellipse 45% 35% at 30% 40%, rgba(42,107,78,0.7) 0%, transparent 60%),
        radial-gradient(ellipse 30% 40% at 65% 55%, rgba(42,107,78,0.6) 0%, transparent 55%),
        radial-gradient(ellipse 25% 20% at 55% 30%, rgba(26,74,53,0.5) 0%, transparent 50%),
        radial-gradient(ellipse 35% 25% at 75% 70%, rgba(42,107,78,0.5) 0%, transparent 50%)
      `,
    }} />
    {/* Cloud layer */}
    <div style={{
      position: 'absolute',
      inset: 0,
      borderRadius: '50%',
      background: `
        radial-gradient(ellipse 50% 12% at 40% 25%, rgba(255,255,255,0.2) 0%, transparent 60%),
        radial-gradient(ellipse 40% 10% at 60% 60%, rgba(255,255,255,0.15) 0%, transparent 55%)
      `,
    }} />
    {/* Atmosphere highlight */}
    <div style={{
      position: 'absolute',
      top: '5%',
      left: '12%',
      width: '40%',
      height: '30%',
      borderRadius: '50%',
      background: 'radial-gradient(ellipse at center, rgba(140,197,224,0.35) 0%, transparent 70%)',
    }} />
  </div>
);

// Cold Planet (Ice planet)
const ColdPlanet = () => (
  <div style={{
    width: '140px',
    height: '140px',
    borderRadius: '50%',
    position: 'relative',
    background: 'radial-gradient(circle at 35% 35%, #D6E4EC 0%, #9FBFCF 50%, #6A8A9A 100%)',
    boxShadow: '0 0 40px 8px rgba(123,164,196,0.15), inset -8px -8px 20px rgba(0,0,0,0.2)',
    overflow: 'hidden',
  }}>
    {/* Ice surface texture */}
    <div style={{
      position: 'absolute',
      inset: 0,
      borderRadius: '50%',
      background: `
        radial-gradient(ellipse 80% 25% at 45% 30%, rgba(214,228,236,0.4) 0%, transparent 50%),
        radial-gradient(ellipse 70% 20% at 55% 55%, rgba(159,191,207,0.3) 0%, transparent 45%),
        radial-gradient(ellipse 60% 15% at 40% 75%, rgba(214,228,236,0.25) 0%, transparent 40%)
      `,
    }} />
    {/* Ice cap highlight */}
    <div style={{
      position: 'absolute',
      top: '0%',
      left: '20%',
      width: '60%',
      height: '20%',
      borderRadius: '50%',
      background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.35) 0%, transparent 70%)',
    }} />
    {/* Atmosphere highlight */}
    <div style={{
      position: 'absolute',
      top: '8%',
      left: '15%',
      width: '35%',
      height: '25%',
      borderRadius: '50%',
      background: 'radial-gradient(ellipse at center, rgba(214,228,236,0.4) 0%, transparent 70%)',
    }} />
  </div>
);

export default GoldilocksZoneDiagram;
